import React, { FC, SyntheticEvent, useEffect, useState } from 'react';

import { Money as MoneyType, WithdrawRequest, WithdrawRequestStatus } from 'api-client';
import { Button, InputMoney, Money, PrimaryButton, StatusBadge } from 'components/common';
import { PrimaryButtonColor } from 'components/common/buttons/decorators/PrimaryButton/PrimaryButton';
import { StatusBadgeTone } from 'components/common/StatusBadge/statusBadgeVariants';
import { MoneyMode } from 'components/common/utils/Money/Money';
import { PaginationLoadMore } from 'components/pagination';
import { createReferralManager } from 'di';
import { formatDate } from 'formatters';
import { useErrorCallback } from 'hooks';

import styles from './ReferralPayout.module.scss';

interface Props {
  totalAccrued: MoneyType;
  totalAvailable: MoneyType;
  withdrawals: WithdrawRequest[];
}

const MIN_AMOUNT = 1000000;
const PAYMENTS_PER_PAGE = 5;
const MIN_AMOUNT_MONEY = new MoneyType(MIN_AMOUNT, 'RUB');

type StatusView = { label: string; tone: StatusBadgeTone };

const statusViews: Record<WithdrawRequestStatus, StatusView> = {
  [WithdrawRequestStatus.WITHDRAWN]: { label: 'Выплачено', tone: 'green' },
  [WithdrawRequestStatus.NEW]: { label: 'В обработке', tone: 'yellow' },
  [WithdrawRequestStatus.CANCELED_BY_USER]: { label: 'Отменено', tone: 'muted' },
  [WithdrawRequestStatus.CANCELED_BY_ADMIN]: { label: 'Отменено', tone: 'muted' },
};

const getStatusView = (status: WithdrawRequestStatus): StatusView =>
  statusViews[status] ?? { label: 'Отменено', tone: 'muted' };

const ReferralPayout: FC<Props> = ({ totalAccrued, totalAvailable, withdrawals }) => {
  const [money, setMoney] = useState<MoneyType>(new MoneyType(MIN_AMOUNT, 'RUB'));
  const [visiblePayments, setVisiblePayments] = useState(PAYMENTS_PER_PAGE);
  const [noMoneyToWithdraw, setNoMoneyToWithdraw] = useState(false);
  const [loading, setLoading] = useState(false);
  const errorCallback = useErrorCallback();

  const getWithdrawnAmount = (withdrawals: WithdrawRequest[]): MoneyType => {
    const total = withdrawals
      .filter((w) => w.status === WithdrawRequestStatus.WITHDRAWN)
      .reduce((sum, w) => sum + w.money.amount, 0);
    return new MoneyType(total, 'RUB');
  };

  const getRequestedAmount = (withdrawals: WithdrawRequest[]): MoneyType => {
    const total = withdrawals.reduce((sum, w) => sum + w.money.amount, 0);
    return new MoneyType(total, 'RUB');
  };

  const getAvailableAmount = (totalAccrued: MoneyType, requested: MoneyType): MoneyType => {
    return new MoneyType(totalAccrued.amount - requested.amount, 'RUB');
  };

  const withdrawnTotal = getWithdrawnAmount(withdrawals);
  const requestedTotal = getRequestedAmount(withdrawals);
  const availableTotal = getAvailableAmount(totalAccrued, requestedTotal);

  const isSubmitDisabled =
    noMoneyToWithdraw || money.amount > availableTotal.amount || money.amount < MIN_AMOUNT;

  const onSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setLoading(true);
    try {
      const manager = await createReferralManager();
      await manager.create(money);
      window.location.reload();
    } catch (err: any) {
      await errorCallback(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setNoMoneyToWithdraw(totalAvailable.amount < MIN_AMOUNT);
  }, [availableTotal]);

  const isEmpty = withdrawals.length === 0;
  const visibleWithdrawals = withdrawals.slice(0, visiblePayments);

  const getNetto = (withdraw: WithdrawRequest): MoneyType =>
    new MoneyType(withdraw.money.amount - withdraw.ndfl.amount, withdraw.money.currencyCode);

  return (
    <div className={styles.payout}>
      <div className={styles.card}>
        <div className={styles.available}>
          <span className={styles.label}>Доступно</span>
          <span className={styles.availableValue}>
            <Money money={totalAvailable} />
          </span>
        </div>
        <div className={styles.stats}>
          <div className={styles.stat}>
            <span className={styles.label}>Начислено</span>
            <span className={styles.statValue}>
              <Money money={totalAccrued} />
            </span>
          </div>
          <div className={styles.stat}>
            <span className={styles.label}>Выплачено</span>
            <span className={styles.statValue}>
              <Money money={withdrawnTotal} />
            </span>
          </div>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.cardTitle}>Вывести средства</div>
        <InputMoney money={money} onValueChanged={(money) => setMoney(money)} />
        <PrimaryButton color={PrimaryButtonColor.GREEN} expanded>
          <Button disabled={isSubmitDisabled} onClick={onSubmit}>
            {loading ? 'Запрос...' : 'Запросить'}
          </Button>
        </PrimaryButton>
        <span className={styles.note}>
          Минимальный размер запроса на вывод{' '}
          <Money mode={MoneyMode.WRITE_OFF} money={MIN_AMOUNT_MONEY} />
        </span>
      </div>

      <div className={styles.card}>
        <div className={styles.cardTitle}>Выплаты</div>
        {isEmpty ? (
          <div className={styles.empty}>Выплаты не найдены</div>
        ) : (
          <div className={styles.payments}>
            {visibleWithdrawals.map((withdraw) => (
              <div className={styles.payment} key={withdraw.uuid}>
                <div className={styles.paymentHeader}>
                  <StatusBadge tone={getStatusView(withdraw.status).tone}>
                    {getStatusView(withdraw.status).label}
                  </StatusBadge>
                  <span className={styles.paymentDate}>{formatDate(withdraw.date)}</span>
                </div>
                <div className={styles.paymentMetrics}>
                  <div className={styles.paymentMetric}>
                    <span className={styles.label}>Брутто</span>
                    <span className={styles.paymentValue}>
                      <Money money={withdraw.money} />
                    </span>
                  </div>
                  <div className={styles.paymentMetric}>
                    <span className={styles.label}>НДФЛ</span>
                    <span className={styles.paymentValue}>
                      <Money money={withdraw.ndfl} />
                    </span>
                  </div>
                  <div className={styles.paymentMetric}>
                    <span className={styles.label}>Нетто</span>
                    <span className={styles.paymentValue}>
                      <Money money={getNetto(withdraw)} />
                    </span>
                  </div>
                </div>
              </div>
            ))}
            <PaginationLoadMore
              loaded={visibleWithdrawals.length}
              total={withdrawals.length}
              page={Math.ceil(visiblePayments / PAYMENTS_PER_PAGE)}
              loading={false}
              onLoadMore={() => setVisiblePayments((prev) => prev + PAYMENTS_PER_PAGE)}
              onReset={() => setVisiblePayments(PAYMENTS_PER_PAGE)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralPayout;
